import React, { FC, useEffect, useRef, useState } from 'react';
import { ref, set } from 'firebase/database';
import { database } from '../../lib/firebase';
import { SeniorLocation } from '../../hooks/useSeniors';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default Leaflet marker icon (broken in webpack builds)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface LocationMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  seniorId: string;
  seniorName: string;
  location?: SeniorLocation;
}

function formatLocationTime(timestamp: number): string {
  const now = Date.now() / 1000;
  const diffSec = Math.floor(now - timestamp);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  return `${diffDays}d ago`;
}

function getAccuracyLabel(accuracy: number): { label: string; color: string } {
  if (accuracy <= 10) return { label: 'Excellent', color: 'text-emerald-600 bg-emerald-50' };
  if (accuracy <= 30) return { label: 'Good', color: 'text-blue-600 bg-blue-50' };
  if (accuracy <= 100) return { label: 'Approximate', color: 'text-amber-600 bg-amber-50' };
  return { label: 'Low accuracy', color: 'text-red-600 bg-red-50' };
}

// Auto-center map when location changes
const MapUpdater: FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap();
  const prevRef = useRef({ lat: 0, lng: 0 });
  useEffect(() => {
    if (prevRef.current.lat !== lat || prevRef.current.lng !== lng) {
      map.setView([lat, lng], 16, { animate: true });
      prevRef.current = { lat, lng };
    }
  }, [lat, lng, map]);
  return null;
};

export const LocationMapModal: FC<LocationMapModalProps> = ({
  isOpen,
  onClose,
  seniorId,
  seniorName,
  location,
}) => {
  const [locating, setLocating] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleLocateNow = () => {
    setLocating(true);
    const commandRef = ref(database, `devices/${seniorId}/commands/locateNow`);
    set(commandRef, {
      requested: true,
      timestamp: Math.floor(Date.now() / 1000),
    });
    // Reset after timeout (phone clears the command, but we reset UI state)
    setTimeout(() => setLocating(false), 8000);
  };

  const hasLocation = location && typeof location.lat === 'number' && typeof location.lng === 'number';
  const accuracy = hasLocation ? getAccuracyLabel(location.accuracy) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 animate-[fadeIn_200ms_ease-out]" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-[slideUp_300ms_ease-out]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="material-symbols-outlined text-blue-600">location_on</span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">{seniorName}'s Location</h2>
              {hasLocation ? (
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm text-gray-500">
                    Updated {formatLocationTime(location.timestamp)}
                  </span>
                  {accuracy && (
                    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${accuracy.color}`}>
                      <span className="material-symbols-outlined text-xs" style={{ fontSize: '12px' }}>
                        {location.accuracy <= 30 ? 'gps_fixed' : 'gps_not_fixed'}
                      </span>
                      {accuracy.label}
                    </span>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mt-0.5">Waiting for GPS data...</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-500">close</span>
          </button>
        </div>

        {/* Map */}
        <div className="flex-1 min-h-[350px] sm:min-h-[400px] relative">
          {hasLocation ? (
            <>
              <MapContainer
                center={[location.lat, location.lng]}
                zoom={16}
                style={{ width: '100%', height: '100%', minHeight: '350px' }}
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <MapUpdater lat={location.lat} lng={location.lng} />
                <Marker position={[location.lat, location.lng]}>
                  <Popup>
                    <strong>{seniorName}</strong>
                    <br />
                    {formatLocationTime(location.timestamp)}
                    <br />
                    <span className="text-xs text-gray-500">
                      {Math.round(location.accuracy)}m accuracy
                    </span>
                  </Popup>
                </Marker>
                {/* Accuracy circle */}
                {location.accuracy > 0 && (
                  <Circle
                    center={[location.lat, location.lng]}
                    radius={location.accuracy}
                    pathOptions={{
                      color: '#3b82f6',
                      fillColor: '#3b82f6',
                      fillOpacity: 0.08,
                      weight: 1.5,
                      dashArray: '4 6',
                    }}
                  />
                )}
              </MapContainer>

              {/* Locating overlay */}
              {locating && (
                <div className="absolute inset-0 bg-white/40 flex items-center justify-center z-[1000] pointer-events-none">
                  <div className="bg-white rounded-xl shadow-lg px-5 py-3 flex items-center gap-3">
                    <span className="animate-spin material-symbols-outlined text-blue-600">progress_activity</span>
                    <span className="text-sm font-medium text-gray-700">Requesting fresh location...</span>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full min-h-[350px] bg-gray-50">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                <span className="material-symbols-outlined text-4xl text-gray-400">location_off</span>
              </div>
              <p className="text-lg font-medium text-gray-500">No location data yet</p>
              <p className="text-sm text-gray-400 mt-1 max-w-xs text-center">
                Location will appear once {seniorName}'s phone sends its first GPS update
              </p>
              <button
                onClick={handleLocateNow}
                disabled={locating}
                className="mt-5 flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors active:scale-[0.98] disabled:opacity-50"
              >
                {locating ? (
                  <>
                    <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                    Locating...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-lg">my_location</span>
                    Request Location
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-4">
            {hasLocation && (
              <>
                <div className="text-sm text-gray-500">
                  <span className="material-symbols-outlined text-xs align-middle mr-1" style={{ fontSize: '14px' }}>pin_drop</span>
                  {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                </div>
                <div className="hidden sm:flex items-center gap-1 text-sm text-gray-400">
                  <span className="material-symbols-outlined text-xs" style={{ fontSize: '14px' }}>straighten</span>
                  ±{Math.round(location.accuracy)}m
                </div>
              </>
            )}
            {!hasLocation && <div />}
          </div>
          <button
            onClick={handleLocateNow}
            disabled={locating}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {locating ? (
              <>
                <span className="animate-spin material-symbols-outlined text-lg">progress_activity</span>
                Locating...
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">my_location</span>
                Locate Now
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationMapModal;
